/**
 * Projects API Service
 */

import { api } from './api';
import type {
  ListProjectsResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  ChunkAppendResponse,
  UpdateProjectRequest,
  ToggleProjectStatusRequest,
} from '../types/api';
import type { Project } from '../types/domain';

export const projectsApi = {
  /**
   * List all projects (admin only).
   *
   * Status accepts either a single value (for back-compat with the old
   * single-select UI) or an array for multi-select — serialized via
   * repeated keys (?status=active&status=inactive).
   */
  list: async (params?: {
    status?: 'active' | 'inactive' | 'all' | Array<'active' | 'inactive'>;
    search?: string;
    limit?: number;
  }): Promise<ListProjectsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      if (Array.isArray(params.status)) {
        for (const s of params.status) queryParams.append('status', s);
      } else {
        queryParams.set('status', params.status);
      }
    }
    if (params?.search) queryParams.set('search', params.search);
    if (params?.limit) queryParams.set('limit', params.limit.toString());

    const query = queryParams.toString();
    const endpoint = query ? `/admin/projects?${query}` : '/admin/projects';

    return api.get<ListProjectsResponse>(endpoint);
  },

  /**
   * Get project by ID
   */
  get: async (projectId: string): Promise<Project> => {
    return api.get<Project>(`/admin/projects/${projectId}`);
  },

  /**
   * Create new project
   */
  create: async (project: CreateProjectRequest): Promise<CreateProjectResponse> => {
    return api.post<CreateProjectResponse>('/admin/projects', project);
  },

  /**
   * Update project
   */
  update: async (
    projectId: string,
    updates: UpdateProjectRequest
  ): Promise<Project> => {
    return api.put<Project>(`/admin/projects/${projectId}`, updates);
  },

  /**
   * Toggle project status (active/inactive)
   */
  toggleStatus: async (
    projectId: string,
    status: 'active' | 'inactive'
  ): Promise<{ status: string }> => {
    const body: ToggleProjectStatusRequest = { status };
    return api.patch<{ status: string }>(`/admin/projects/${projectId}/status`, body);
  },

  /**
   * Delete project (soft delete)
   */
  delete: async (projectId: string): Promise<void> => {
    return api.delete<void>(`/admin/projects/${projectId}`);
  },

  /**
   * Continue chunked project creation (append next batch of scenarios)
   */
  continueCreation: async (data: {
    _continue_project_id: string;
    _next_offset: number;
    job_id: string;
    name: string;
    project_number: string;
    testing_phase: string;
    ict_responsible: string;
    user_mappings?: CreateProjectRequest['user_mappings'];
    [key: string]: unknown;
  }): Promise<ChunkAppendResponse> => {
    return api.post<ChunkAppendResponse>('/admin/projects', data);
  },

  /**
   * Create multiple projects in batch (from wizard)
   */
  createBatch: async (data: {
    projects: Array<CreateProjectRequest>;
  }): Promise<{
    project_ids: string[];
    count: number;
    stats: {
      total_scenarios: number;
      total_steps: number;
      total_mappings: number;
    };
  }> => {
    return api.post('/admin/projects/batch', data);
  },
};
